import { useState } from "react";
import { CalendarDays, Check, Plus } from "lucide-react";
import { Btn, Field, Modal } from "@/components/kit";
import { useCreateEvent, useEvents } from "@/hooks/queries";
import { fmtDate } from "@/lib/toto";

export function EventForm({ initial, onSubmit, loading, submitLabel = "Create Event", prefix = "event" }) {
  const [f, setF] = useState({ name: initial?.name ?? "", eventDate: initial?.eventDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10), location: initial?.location ?? "", description: initial?.description ?? "" });
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = () => {
    if (!f.name.trim()) return setErr("Please enter an event name.");
    if (!f.eventDate) return setErr("Please choose a date.");
    setErr(null);
    onSubmit({ name: f.name.trim(), eventDate: f.eventDate, location: f.location.trim() || undefined, description: f.description.trim() || undefined });
  };
  return (
    <div className="flex flex-col gap-4">
      <Field label="Event Name *" placeholder="e.g. Startup Networking Mixer" value={f.name} onChange={set("name")} testid={`${prefix}-name`} />
      <Field label="Date *" type="date" value={f.eventDate} onChange={set("eventDate")} testid={`${prefix}-date`} />
      <Field label="Location" placeholder="Venue or city" value={f.location} onChange={set("location")} testid={`${prefix}-location`} />
      <Field label="Description" textarea placeholder="Notes about this event" value={f.description} onChange={set("description")} testid={`${prefix}-description`} />
      {err && <p className="text-[14px] text-[#DC2626]" data-testid={`${prefix}-error`}>{err}</p>}
      <Btn onClick={submit} loading={loading} testid={`${prefix}-submit`}>{submitLabel}</Btn>
    </div>
  );
}

export function CreateEventModal({ open, onClose, onCreated }) {
  const create = useCreateEvent();
  const [err, setErr] = useState(null);
  return (
    <Modal open={open} onClose={onClose} title="Create Event" testid="create-event-modal">
      <EventForm loading={create.isPending} onSubmit={async (b) => {
        try { const ev = await create.mutateAsync(b); onCreated?.(ev); onClose(); } catch (e) { setErr(e.message); }
      }} prefix="create-event" />
      {err && <p className="text-[14px] text-[#DC2626]">{err}</p>}
    </Modal>
  );
}

export default function EventPicker({ open, onClose, selectedIds, onPick, allowNone, testid = "event-picker" }) {
  const events = useEvents({ sort: "date" }).data ?? [];
  const [creating, setCreating] = useState(false);
  return (
    <>
      <Modal open={open && !creating} onClose={onClose} title="Add to Event" testid={testid}>
        <div className="max-h-[360px] overflow-y-auto -mx-2">
          {allowNone && (
            <button className="w-full flex items-center justify-between px-3 h-12 rounded-xl hover:bg-[#F8FAFC] text-[15px] font-medium" onClick={() => onPick(null)} data-testid="event-option-none">
              No Event {selectedIds.size === 0 && <Check className="w-5 h-5 text-[#0000EC]" />}
            </button>
          )}
          {events.map((ev) => (
            <button key={ev.id} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F8FAFC] text-left" onClick={() => onPick(ev)} data-testid={`event-option-${ev.id}`}>
              <CalendarDays className="w-5 h-5 text-[#0000EC]" />
              <span className="flex-1 min-w-0"><span className="block text-[15px] font-semibold truncate">{ev.name}</span><span className="text-[13px] text-[#64748B]">{fmtDate(ev.eventDate)}</span></span>
              {selectedIds.has(ev.id) && <Check className="w-5 h-5 text-[#0000EC]" />}
            </button>
          ))}
          {!events.length && <p className="px-3 py-4 text-[14px] text-[#64748B]">No events created yet.</p>}
        </div>
        <button className="flex items-center gap-2 t-link h-11" onClick={() => setCreating(true)} data-testid="event-option-create"><Plus className="w-5 h-5" />Create New Event</button>
      </Modal>
      <CreateEventModal open={creating} onClose={() => setCreating(false)} onCreated={(ev) => onPick(ev)} />
    </>
  );
}
